/**
 * WeatherDetail — rich weather panel shown inside the Home bottom sheet.
 *
 * Aggregates three Open-Meteo endpoints in parallel:
 *   • /v1/forecast     – current, hourly (UV), daily (7 giorni + sunrise/sunset)
 *   • /v1/air-quality  – European AQI + PM10 + PM2.5 + UV
 *   • /v1/marine       – wave height + sea surface temp (skipped inland)
 *
 * The design mirrors the tile grid tokens (rounded-3xl white cards, zinc-100
 * borders, sky/rose/emerald accents) so it feels native to the Welcome Book.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "./TechSheet.css";
import {
  Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, CloudSun,
  Wind, Droplets, Gauge, Sunrise, Sunset, Waves, Thermometer, Compass, Eye, MapPin,
} from "lucide-react";

const T = {
  it: { feels: "Percepita", wind: "Vento", gusts: "Raffiche", humidity: "Umidità", pressure: "Pressione", uv: "Indice UV", clouds: "Nuvole", sunrise: "Alba", sunset: "Tramonto", air: "Qualità dell'aria", aqi: "Indice europeo", pm10: "PM10", pm25: "PM2,5", sea: "Mare", wave: "Altezza onde", seaTemp: "Temperatura mare", forecast: "Previsioni 7 giorni", today: "Oggi", fromNE: "Da", low: "Bassa", moderate: "Moderata", high: "Alta", veryHigh: "Molto alta", extreme: "Estrema", good: "Buona", fair: "Discreta", poor: "Scarsa", veryPoor: "Pessima", calm: "Calmo" },
  en: { feels: "Feels like", wind: "Wind", gusts: "Gusts", humidity: "Humidity", pressure: "Pressure", uv: "UV Index", clouds: "Clouds", sunrise: "Sunrise", sunset: "Sunset", air: "Air quality", aqi: "European index", pm10: "PM10", pm25: "PM2.5", sea: "Sea", wave: "Wave height", seaTemp: "Sea temperature", forecast: "7-day forecast", today: "Today", fromNE: "From", low: "Low", moderate: "Moderate", high: "High", veryHigh: "Very high", extreme: "Extreme", good: "Good", fair: "Fair", poor: "Poor", veryPoor: "Very poor", calm: "Calm" },
  fr: { feels: "Ressentie", wind: "Vent", gusts: "Rafales", humidity: "Humidité", pressure: "Pression", uv: "Indice UV", clouds: "Nuages", sunrise: "Lever", sunset: "Coucher", air: "Qualité de l'air", aqi: "Indice européen", pm10: "PM10", pm25: "PM2,5", sea: "Mer", wave: "Hauteur des vagues", seaTemp: "Température de la mer", forecast: "Prévisions 7 jours", today: "Aujourd'hui", fromNE: "De", low: "Faible", moderate: "Modéré", high: "Élevé", veryHigh: "Très élevé", extreme: "Extrême", good: "Bonne", fair: "Correcte", poor: "Mauvaise", veryPoor: "Très mauvaise", calm: "Calme" },
  es: { feels: "Sensación", wind: "Viento", gusts: "Rachas", humidity: "Humedad", pressure: "Presión", uv: "Índice UV", clouds: "Nubes", sunrise: "Amanecer", sunset: "Atardecer", air: "Calidad del aire", aqi: "Índice europeo", pm10: "PM10", pm25: "PM2,5", sea: "Mar", wave: "Altura de las olas", seaTemp: "Temperatura del mar", forecast: "Pronóstico 7 días", today: "Hoy", fromNE: "De", low: "Bajo", moderate: "Moderado", high: "Alto", veryHigh: "Muy alto", extreme: "Extremo", good: "Buena", fair: "Aceptable", poor: "Mala", veryPoor: "Muy mala", calm: "Calmado" },
  de: { feels: "Gefühlt", wind: "Wind", gusts: "Böen", humidity: "Feuchte", pressure: "Druck", uv: "UV-Index", clouds: "Wolken", sunrise: "Aufgang", sunset: "Untergang", air: "Luftqualität", aqi: "Europäischer Index", pm10: "PM10", pm25: "PM2,5", sea: "Meer", wave: "Wellenhöhe", seaTemp: "Meerestemperatur", forecast: "7-Tage-Vorhersage", today: "Heute", fromNE: "Von", low: "Niedrig", moderate: "Mäßig", high: "Hoch", veryHigh: "Sehr hoch", extreme: "Extrem", good: "Gut", fair: "Mäßig", poor: "Schlecht", veryPoor: "Sehr schlecht", calm: "Ruhig" },
};

const weatherMap = (code) => {
  const c = Number(code);
  if (c === 0) return { Icon: Sun, color: "#F59E0B", label: { it: "Sereno", en: "Clear", fr: "Ensoleillé", es: "Despejado", de: "Klar" } };
  if ([1, 2].includes(c)) return { Icon: CloudSun, color: "#3B82F6", label: { it: "Poco nuvoloso", en: "Partly cloudy", fr: "Peu nuageux", es: "Parcialmente nublado", de: "Teilweise bewölkt" } };
  if (c === 3) return { Icon: Cloud, color: "#64748B", label: { it: "Nuvoloso", en: "Cloudy", fr: "Nuageux", es: "Nublado", de: "Bewölkt" } };
  if ([45, 48].includes(c)) return { Icon: CloudFog, color: "#94A3B8", label: { it: "Nebbia", en: "Fog", fr: "Brouillard", es: "Niebla", de: "Nebel" } };
  if ([51, 53, 55, 61, 63, 65, 80, 81, 82].includes(c)) return { Icon: CloudRain, color: "#2563EB", label: { it: "Pioggia", en: "Rain", fr: "Pluie", es: "Lluvia", de: "Regen" } };
  if ([71, 73, 75, 77, 85, 86].includes(c)) return { Icon: CloudSnow, color: "#0EA5E9", label: { it: "Neve", en: "Snow", fr: "Neige", es: "Nieve", de: "Schnee" } };
  if ([95, 96, 99].includes(c)) return { Icon: CloudLightning, color: "#7C3AED", label: { it: "Temporale", en: "Thunderstorm", fr: "Orage", es: "Tormenta", de: "Gewitter" } };
  return { Icon: Cloud, color: "#64748B", label: { it: "Nuvoloso", en: "Cloudy", fr: "Nuageux", es: "Nublado", de: "Bewölkt" } };
};

const dayLong = (dateStr, lang) => new Date(dateStr).toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "short" });
const hhmm = (iso) => { try { return iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—"; } catch { return "—"; } };

// 16-wind rose direction: converts degrees to a short compass label.
const compassDir = (deg) => {
  if (deg == null) return "—";
  const dirs = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
  return dirs[Math.round(((deg % 360) / 22.5)) % 16];
};

// UV index → severity label (WHO scale).
const uvBucket = (uv, t) => {
  if (uv == null) return "—";
  if (uv < 3) return t.low;
  if (uv < 6) return t.moderate;
  if (uv < 8) return t.high;
  if (uv < 11) return t.veryHigh;
  return t.extreme;
};

// EAQI 0-100 buckets (Open-Meteo european_aqi).
const aqiBucket = (v, t) => {
  if (v == null) return { label: "—", color: "#71717A" };
  if (v <= 20) return { label: t.good, color: "#10B981" };
  if (v <= 40) return { label: t.fair, color: "#84CC16" };
  if (v <= 60) return { label: t.moderate, color: "#F59E0B" };
  if (v <= 80) return { label: t.poor, color: "#EF4444" };
  return { label: t.veryPoor, color: "#7C3AED" };
};

// Small primitives — mirror the existing Welcome Book card style.
const StatCard = ({ icon: Icon, label, value, sub, tint = "#0F172A" }) => (
  <div className="rounded-2xl bg-white border border-zinc-100 p-3 shadow-sm">
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className="w-3.5 h-3.5" style={{ color: tint }} strokeWidth={2} />
      <p className="text-[9.5px] font-black uppercase tracking-wider text-zinc-500">{label}</p>
    </div>
    <p className="text-[17px] font-black text-zinc-900 leading-none">{value}</p>
    {sub && <p className="text-[10px] text-zinc-400 font-medium mt-1 leading-tight">{sub}</p>}
  </div>
);

export default function WeatherDetail({ lat, lng, lang = "it", locationLabel, accent, gradient, hideHero }) {
  const t = T[lang] || T.it;
  const [forecast, setForecast] = useState(null);
  const [air, setAir] = useState(null);
  const [marine, setMarine] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!lat || !lng) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const forecastUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,wind_direction_10m,wind_gusts_10m,relative_humidity_2m,pressure_msl,cloud_cover&daily=temperature_2m_max,temperature_2m_min,weather_code,precipitation_probability_max,precipitation_sum,wind_speed_10m_max,sunrise,sunset,uv_index_max&timezone=auto&forecast_days=7`;
        const airUrl = `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lng}&current=european_aqi,pm10,pm2_5,uv_index&timezone=auto`;
        const marineUrl = `https://marine-api.open-meteo.com/v1/marine?latitude=${lat}&longitude=${lng}&current=wave_height,wave_direction,wave_period,sea_surface_temperature&timezone=auto`;
        const [f, a, m] = await Promise.allSettled([
          fetch(forecastUrl).then((r) => r.json()),
          fetch(airUrl).then((r) => r.json()),
          fetch(marineUrl).then((r) => r.json()),
        ]);
        if (cancelled) return;
        if (f.status === "fulfilled") setForecast(f.value);
        if (a.status === "fulfilled") setAir(a.value);
        // Marine returns 400 for inland locations — silently ignore.
        if (m.status === "fulfilled" && m.value?.current?.wave_height != null) setMarine(m.value);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lat, lng]);

  if (!lat || !lng) return null;
  if (loading) {
    return (
      <div className="py-6 flex flex-col items-center gap-3">
        <div className="animate-spin w-6 h-6 border-2 border-zinc-900 border-t-transparent rounded-full" />
        <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Loading…</p>
      </div>
    );
  }
  if (!forecast?.current) return null;

  const cur = forecast.current;
  const daily = forecast.daily;
  const meta = weatherMap(cur.weather_code);
  const CurIcon = meta.Icon;
  const uvToday = daily?.uv_index_max?.[0];
  const airCur = air?.current;
  const aqi = aqiBucket(airCur?.european_aqi, t);
  const marCur = marine?.current;

  return (
    <div className="tk" style={{ "--tk-accent": accent || meta.color, "--tk-accent-soft": `${accent || meta.color}55` }}>
      {!hideHero && <>
      <div className="tk-checker" />
      {/* Hero header */}
      <div className="tk-head tk-head-vibrant" style={{ background: gradient || `linear-gradient(135deg, ${meta.color}, ${meta.color}cc)` }}>
        <div className="tk-head-row">
          <motion.div animate={{ y: [0, -5, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            style={{ filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.28))" }}>
            <CurIcon className="w-14 h-14 text-white" strokeWidth={1.4} />
          </motion.div>
          <div className="min-w-0">
            <div className="tk-kicker"><span className="dot" />{lang === "it" ? "METEO" : lang === "de" ? "WETTER" : lang === "fr" ? "MÉTÉO" : lang === "es" ? "TIEMPO" : "WEATHER"}</div>
            <div className="tk-title" style={{ fontSize: "46px" }}>{Math.round(cur.temperature_2m)}°</div>
          </div>
        </div>
        <div className="tk-sub">
          {(meta.label[lang] || meta.label.it)}{locationLabel ? ` · ${locationLabel}` : ""}
        </div>
      </div>
      <div className="tk-perf"><span /></div>
      </>}

      <div className="tk-body space-y-4">
      {/* Now — 6 stat cards */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard icon={Thermometer} label={t.feels} value={`${Math.round(cur.apparent_temperature)}°`} tint="#F97316" />
        <StatCard icon={Wind} label={t.wind}
          value={`${Math.round(cur.wind_speed_10m)} km/h`}
          sub={`${t.fromNE} ${compassDir(cur.wind_direction_10m)}`} tint="#0EA5E9" />
        <StatCard icon={Wind} label={t.gusts} value={cur.wind_gusts_10m != null ? `${Math.round(cur.wind_gusts_10m)} km/h` : "—"} tint="#6366F1" />
        <StatCard icon={Droplets} label={t.humidity} value={`${Math.round(cur.relative_humidity_2m)}%`} tint="#3B82F6" />
        <StatCard icon={Gauge} label={t.pressure} value={cur.pressure_msl != null ? `${Math.round(cur.pressure_msl)} hPa` : "—"} tint="#8B5CF6" />
        <StatCard icon={Eye} label={t.clouds} value={cur.cloud_cover != null ? `${Math.round(cur.cloud_cover)}%` : "—"} tint="#64748B" />
      </div>

      {/* Sunrise / Sunset */}
      {daily?.sunrise?.[0] && (
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-100 border border-amber-100 p-3 flex items-center gap-3">
            <Sunrise className="w-6 h-6 text-amber-600" strokeWidth={1.7} />
            <div>
              <p className="text-[9.5px] font-black uppercase tracking-wider text-amber-700">{t.sunrise}</p>
              <p className="text-[17px] font-black text-zinc-900 leading-tight">{hhmm(daily.sunrise[0])}</p>
            </div>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-orange-50 to-rose-100 border border-orange-100 p-3 flex items-center gap-3">
            <Sunset className="w-6 h-6 text-rose-600" strokeWidth={1.7} />
            <div>
              <p className="text-[9.5px] font-black uppercase tracking-wider text-rose-700">{t.sunset}</p>
              <p className="text-[17px] font-black text-zinc-900 leading-tight">{hhmm(daily.sunset[0])}</p>
            </div>
          </div>
        </div>
      )}

      {/* Air quality */}
      {airCur && airCur.european_aqi != null && (
        <div className="rounded-3xl bg-white border border-zinc-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${aqi.color}22` }}>
              <Wind className="w-4 h-4" style={{ color: aqi.color }} strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-black text-zinc-900 leading-tight">{t.air}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: aqi.color }}>{aqi.label}</p>
            </div>
            <p className="text-[24px] font-black" style={{ color: aqi.color }}>{Math.round(airCur.european_aqi)}</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-zinc-50 p-2 text-center">
              <p className="text-[9px] font-black uppercase tracking-wider text-zinc-500">{t.pm10}</p>
              <p className="text-[13px] font-black text-zinc-900 mt-0.5">{airCur.pm10 != null ? `${airCur.pm10.toFixed(1)}` : "—"} <span className="text-[9px] text-zinc-400">µg/m³</span></p>
            </div>
            <div className="rounded-xl bg-zinc-50 p-2 text-center">
              <p className="text-[9px] font-black uppercase tracking-wider text-zinc-500">{t.pm25}</p>
              <p className="text-[13px] font-black text-zinc-900 mt-0.5">{airCur.pm2_5 != null ? `${airCur.pm2_5.toFixed(1)}` : "—"} <span className="text-[9px] text-zinc-400">µg/m³</span></p>
            </div>
            <div className="rounded-xl bg-zinc-50 p-2 text-center">
              <p className="text-[9px] font-black uppercase tracking-wider text-zinc-500">{t.uv}</p>
              <p className="text-[13px] font-black text-zinc-900 mt-0.5">{uvToday != null ? uvToday.toFixed(1) : (airCur.uv_index != null ? airCur.uv_index.toFixed(1) : "—")} <span className="text-[9px] text-zinc-400">{uvBucket(uvToday ?? airCur.uv_index, t)}</span></p>
            </div>
          </div>
        </div>
      )}

      {/* Marine — only for coastal locations */}
      {marCur && (
        <div className="rounded-3xl bg-gradient-to-br from-sky-50 to-cyan-100 border border-sky-100 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-sky-600 flex items-center justify-center">
              <Waves className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <p className="text-[13px] font-black text-zinc-900 leading-tight">{t.sea}</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-white/80 p-2 text-center">
              <p className="text-[9px] font-black uppercase tracking-wider text-sky-700">{t.wave}</p>
              <p className="text-[15px] font-black text-zinc-900 mt-0.5">{marCur.wave_height != null ? `${marCur.wave_height.toFixed(1)} m` : t.calm}</p>
            </div>
            <div className="rounded-xl bg-white/80 p-2 text-center">
              <p className="text-[9px] font-black uppercase tracking-wider text-sky-700">Direzione</p>
              <p className="text-[15px] font-black text-zinc-900 mt-0.5">{compassDir(marCur.wave_direction)}</p>
            </div>
            <div className="rounded-xl bg-white/80 p-2 text-center">
              <p className="text-[9px] font-black uppercase tracking-wider text-sky-700">{t.seaTemp}</p>
              <p className="text-[15px] font-black text-zinc-900 mt-0.5">{marCur.sea_surface_temperature != null ? `${marCur.sea_surface_temperature.toFixed(1)}°C` : "—"}</p>
            </div>
          </div>
        </div>
      )}

      {/* 7-day forecast */}
      {daily?.time && (
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.15em] text-zinc-400 mb-2 px-1">{t.forecast}</p>
          <div className="space-y-2">
            {daily.time.map((d, i) => {
              const dm = weatherMap(daily.weather_code[i]);
              const DIco = dm.Icon;
              const isToday = i === 0;
              return (
                <motion.div key={d}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  className={`flex items-center gap-3 p-3 rounded-2xl transition-colors ${isToday ? "bg-white shadow-sm border border-zinc-100" : "bg-zinc-50"}`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${dm.color}15` }}>
                    <DIco className="w-5 h-5" style={{ color: dm.color }} strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-zinc-900 capitalize truncate">{isToday ? t.today : dayLong(d, lang)}</p>
                    <p className="text-[10px] text-zinc-500 truncate">{dm.label[lang] || dm.label.it}</p>
                  </div>
                  {daily.precipitation_probability_max?.[i] > 20 && (
                    <div className="flex items-center gap-1 text-[10px] font-bold text-sky-600 shrink-0">
                      <Droplets className="w-3 h-3" />
                      {daily.precipitation_probability_max[i]}%
                    </div>
                  )}
                  <div className="flex items-baseline gap-1.5 shrink-0">
                    <span className="text-sm font-black text-zinc-900">{Math.round(daily.temperature_2m_max[i])}°</span>
                    <span className="text-xs text-zinc-400 font-medium">{Math.round(daily.temperature_2m_min[i])}°</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-center text-[9px] text-zinc-400 mt-2 font-medium">Powered by Open-Meteo</p>
      </div>
    </div>
  );
}
